import store from '../store/store';
import { updateEventCount } from '../store/slices/debugSlice';

class DebugRecorder {
  constructor() {
    this.isRecording = false;
    this.events = [];
    this.networkCalls = [];
    this.stateSnapshots = [];
    this.consoleOutput = [];
    this.sessionMetadata = {};
    this.maxEvents = 10000;
    this.listeners = new Map();
    this.originalFetch = null;
    this.originalXHR = null;
    this.consoleOverrides = null;
    this.dbName = 'DebugSessions';
    this.dbVersion = 1;
    this.db = null;

    this.initializeDB();
  }

  async initializeDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('sessions')) {
          db.createObjectStore('sessions', { keyPath: 'id', autoIncrement: true });
        }
      };
    });
  }

  startRecording() {
    if (this.isRecording) return;

    this.isRecording = true;
    this.events = [];
    this.networkCalls = [];
    this.stateSnapshots = [];
    this.consoleOutput = [];
    this.sessionMetadata = {
      startTime: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    };

    this.attachEventListeners();
    this.interceptNetwork();
    this.interceptConsole();
    this.subscribeToStore();

    // Initial state snapshot
    this.captureStateSnapshot('initial');
  }

  async stopRecording() {
    if (!this.isRecording) return;

    this.isRecording = false;
    this.detachEventListeners();
    this.restoreNetwork();
    this.restoreConsole();
    this.unsubscribeFromStore();

    // Save session to IndexedDB and wait for completion
    await this.saveSession();
  }

  attachEventListeners() {
    // Only record meaningful events - skip mousemove (too noisy)
    const events = [
      'click', 'dblclick',
      'keydown', 'keyup',
      'change', 'input', 'submit',
      'focus', 'blur',
      'error', 'unhandledrejection'
    ];

    events.forEach(eventType => {
      const handler = (event) => this.recordEvent(eventType, event);
      document.addEventListener(eventType, handler, true);
      this.listeners.set(eventType, handler);
    });

    // Navigation events
    window.addEventListener('popstate', this.recordNavigationEvent.bind(this));

    // Intercept pushState and replaceState
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      this.recordNavigationEvent({ type: 'pushState', url: args[2] });
      return originalPushState.apply(history, args);
    };

    history.replaceState = (...args) => {
      this.recordNavigationEvent({ type: 'replaceState', url: args[2] });
      return originalReplaceState.apply(history, args);
    };

    this.listeners.set('pushState', originalPushState);
    this.listeners.set('replaceState', originalReplaceState);
  }

  detachEventListeners() {
    this.listeners.forEach((handler, eventType) => {
      if (eventType === 'pushState') {
        history.pushState = handler;
      } else if (eventType === 'replaceState') {
        history.replaceState = handler;
      } else {
        document.removeEventListener(eventType, handler, true);
      }
    });
    this.listeners.clear();
  }

  recordEvent(type, event) {
    if (!this.isRecording) return;

    const sanitizedEvent = {
      type,
      timestamp: Date.now(),
      target: this.getTargetInfo(event.target),
      ...(type.includes('key') && { key: event.key, code: event.code }),
      ...(type.includes('mouse') && { x: event.clientX, y: event.clientY }),
      ...(type === 'scroll' && { scrollTop: event.target.scrollTop, scrollLeft: event.target.scrollLeft }),
      ...(type === 'change' || type === 'input' && { value: this.sanitizeValue(event.target) }),
      ...(type === 'error' && { message: event.message, stack: event.error?.stack }),
    };

    this.addEvent(sanitizedEvent);
  }

  recordNavigationEvent(event) {
    this.addEvent({
      type: 'navigation',
      timestamp: Date.now(),
      eventType: event.type,
      url: event.url || window.location.href,
    });
  }

  getTargetInfo(element) {
    if (!element || !element.tagName) return null;

    // Handle SVG elements which have SVGAnimatedString for className/href
    // These can't be cloned to IndexedDB, so we convert to strings
    const getStringValue = (val) => {
      if (val === null || val === undefined) return null;
      if (typeof val === 'string') return val;
      if (val.baseVal !== undefined) return val.baseVal; // SVGAnimatedString
      return String(val);
    };

    return {
      tagName: element.tagName,
      id: element.id,
      className: getStringValue(element.className),
      name: element.name,
      type: element.type,
      href: getStringValue(element.href),
      text: element.innerText?.substring(0, 100),
      xpath: this.getXPath(element),
    };
  }

  getXPath(element) {
    if (!element) return '';
    if (element.id) return `//*[@id="${element.id}"]`;

    const parts = [];
    while (element && element.nodeType === Node.ELEMENT_NODE) {
      let index = 0;
      let sibling = element.previousSibling;
      while (sibling) {
        if (sibling.nodeType === Node.ELEMENT_NODE && sibling.nodeName === element.nodeName) {
          index++;
        }
        sibling = sibling.previousSibling;
      }
      const tagName = element.nodeName.toLowerCase();
      const xpathIndex = index > 0 ? `[${index + 1}]` : '';
      parts.unshift(`${tagName}${xpathIndex}`);
      element = element.parentNode;
    }
    return `/${parts.join('/')}`;
  }

  sanitizeValue(element) {
    const sensitiveFields = ['password', 'secret', 'token', 'key', 'credit', 'ssn'];
    const fieldName = (element.name || element.id || '').toLowerCase();

    if (sensitiveFields.some(field => fieldName.includes(field))) {
      return '[REDACTED]';
    }

    return element.value?.substring(0, 1000);
  }

  interceptNetwork() {
    // Intercept fetch - bind to window to preserve 'this' context
    this.originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const requestId = Date.now() + Math.random();
      const [url, options = {}] = args;

      const requestData = {
        id: requestId,
        type: 'fetch',
        timestamp: Date.now(),
        url: typeof url === 'string' ? url : url.url,
        method: options.method || 'GET',
        headers: this.sanitizeHeaders(options.headers),
        body: this.sanitizeBody(options.body),
      };

      this.addNetworkCall(requestData);

      try {
        const response = await this.originalFetch(...args);
        const responseClone = response.clone();

        const responseData = {
          ...requestData,
          responseTimestamp: Date.now(),
          status: response.status,
          statusText: response.statusText,
          headers: this.sanitizeHeaders(Object.fromEntries(response.headers.entries())),
        };

        try {
          const body = await responseClone.text();
          responseData.responseBody = this.sanitizeBody(body);
        } catch (e) {
          responseData.responseBody = '[Unable to read response]';
        }

        this.updateNetworkCall(requestId, responseData);
        return response;
      } catch (error) {
        this.updateNetworkCall(requestId, {
          error: error.message,
          stack: error.stack,
        });
        throw error;
      }
    };

    // Intercept XMLHttpRequest
    const OriginalXHR = XMLHttpRequest;
    const recorder = this;

    window.XMLHttpRequest = function() {
      const xhr = new OriginalXHR();
      const requestId = Date.now() + Math.random();
      let requestData = {};

      const originalOpen = xhr.open;
      xhr.open = function(method, url, ...args) {
        requestData = {
          id: requestId,
          type: 'xhr',
          timestamp: Date.now(),
          method,
          url,
        };
        return originalOpen.apply(xhr, [method, url, ...args]);
      };

      const originalSend = xhr.send;
      xhr.send = function(body) {
        requestData.body = recorder.sanitizeBody(body);
        requestData.headers = recorder.sanitizeHeaders(xhr.getAllResponseHeaders());
        recorder.addNetworkCall(requestData);

        xhr.addEventListener('load', () => {
          recorder.updateNetworkCall(requestId, {
            responseTimestamp: Date.now(),
            status: xhr.status,
            statusText: xhr.statusText,
            responseBody: recorder.sanitizeBody(xhr.responseText),
            headers: recorder.sanitizeHeaders(xhr.getAllResponseHeaders()),
          });
        });

        xhr.addEventListener('error', () => {
          recorder.updateNetworkCall(requestId, {
            error: 'Network error',
          });
        });

        return originalSend.apply(xhr, [body]);
      };

      return xhr;
    };

    this.originalXHR = OriginalXHR;
  }

  restoreNetwork() {
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }

    if (this.originalXHR) {
      window.XMLHttpRequest = this.originalXHR;
      this.originalXHR = null;
    }
  }

  sanitizeHeaders(headers) {
    if (!headers) return {};

    const blocked = ['authorization', 'cookie', 'x-api-key', 'x-auth-token'];
    const sanitized = {};

    const headerObj = headers instanceof Headers
      ? Object.fromEntries(headers.entries())
      : typeof headers === 'string'
        ? headers.split('\n').reduce((acc, line) => {
            const [key, value] = line.split(':').map(s => s.trim());
            if (key) acc[key] = value;
            return acc;
          }, {})
        : headers;

    Object.entries(headerObj).forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();
      if (blocked.includes(lowerKey) || lowerKey.includes('token') || lowerKey.includes('secret')) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = value;
      }
    });

    return sanitized;
  }

  sanitizeBody(body) {
    if (!body) return null;

    try {
      const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
      const parsed = JSON.parse(bodyStr);

      const sensitive = /\b(?:password|secret|token|key|credential|api[_-]?key|auth|session|csrf)\b/i;

      const sanitize = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(sanitize);
        }
        if (obj && typeof obj === 'object') {
          const result = {};
          Object.entries(obj).forEach(([key, value]) => {
            if (sensitive.test(key)) {
              result[key] = '[REDACTED]';
            } else {
              result[key] = sanitize(value);
            }
          });
          return result;
        }
        return obj;
      };

      return sanitize(parsed);
    } catch {
      // Not JSON, truncate if too long
      const str = body.toString();
      return str.length > 5000 ? str.substring(0, 5000) + '... [truncated]' : str;
    }
  }

  interceptConsole() {
    const methods = ['log', 'warn', 'error', 'info', 'debug'];
    this.consoleOverrides = {};

    methods.forEach(method => {
      this.consoleOverrides[method] = console[method];
      console[method] = (...args) => {
        if (this.isRecording) {
          this.addConsoleOutput({
            type: method,
            timestamp: Date.now(),
            args: args.map(arg => {
              try {
                return typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg);
              } catch {
                return '[Circular or complex object]';
              }
            }),
          });
        }
        return this.consoleOverrides[method](...args);
      };
    });
  }

  restoreConsole() {
    if (this.consoleOverrides) {
      Object.entries(this.consoleOverrides).forEach(([method, original]) => {
        console[method] = original;
      });
      this.consoleOverrides = null;
    }
  }

  subscribeToStore() {
    // Track previous error state to detect new errors
    let previousErrors = {};
    // Prevent re-entrant calls that cause infinite loops
    let isProcessing = false;

    // Helper to add error without triggering Redux dispatch
    const addErrorSilently = (key, value) => {
      this.consoleOutput.push({
        type: 'error',
        timestamp: Date.now(),
        args: [`[Redux ${key}] ${value}`],
      });
      // Don't call updateStore() here to avoid infinite loop
    };

    // Capture any existing errors at the start of recording
    // This catches errors that were set before recording started
    const initialState = store.getState();
    const initialErrors = this.extractErrors(initialState);
    Object.entries(initialErrors).forEach(([key, value]) => {
      if (value) {
        addErrorSilently(key, value);
      }
    });
    previousErrors = { ...initialErrors };

    this.unsubscribe = store.subscribe(() => {
      // Prevent re-entrant calls
      if (isProcessing || !this.isRecording) return;
      isProcessing = true;

      try {
        const state = store.getState();

        // Check for new errors in Redux state and log them
        // This ensures UI-displayed errors are captured by the debug recorder
        const errorFields = this.extractErrors(state);
        Object.entries(errorFields).forEach(([key, value]) => {
          if (value && value !== previousErrors[key]) {
            // New error detected - add silently to avoid loop
            addErrorSilently(key, value);
          }
        });
        previousErrors = errorFields;

        // Throttle state snapshots to max 1 per second
        if (!this.lastSnapshotTime || Date.now() - this.lastSnapshotTime > 1000) {
          this.captureStateSnapshot('change');
          this.lastSnapshotTime = Date.now();
        }
      } finally {
        isProcessing = false;
      }
    });
  }

  // Extract error fields from Redux state
  extractErrors(state) {
    const errors = {};

    // Helper to convert error to string
    const errorToString = (err) => {
      if (!err) return null;
      if (typeof err === 'string') return err;
      if (err.message) return err.message; // Error objects
      try {
        return JSON.stringify(err);
      } catch {
        return String(err);
      }
    };

    // Check common error field patterns across slices
    Object.entries(state).forEach(([sliceName, sliceState]) => {
      if (sliceState && typeof sliceState === 'object') {
        // Common error field names
        if (sliceState.error) {
          errors[`${sliceName}.error`] = errorToString(sliceState.error);
        }
        if (sliceState.queryError) {
          errors[`${sliceName}.queryError`] = errorToString(sliceState.queryError);
        }
        if (sliceState.lastError) {
          errors[`${sliceName}.lastError`] = errorToString(sliceState.lastError);
        }
        if (sliceState.errorMessage) {
          errors[`${sliceName}.errorMessage`] = errorToString(sliceState.errorMessage);
        }
      }
    });

    return errors;
  }

  unsubscribeFromStore() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  captureStateSnapshot(trigger) {
    const state = store.getState();

    // Sanitize sensitive data from state
    const sanitizedState = {
      ...state,
      auth: state.auth ? {
        ...state.auth,
        user: state.auth.user ? {
          ...state.auth.user,
          // Keep non-sensitive fields
          id: state.auth.user.id,
          email: '[REDACTED]',
          isGodMode: state.auth.user.isGodMode,
        } : null,
        isAuthenticated: state.auth.isAuthenticated,
        activeTenant: state.auth.activeTenant,
      } : {},
    };

    this.stateSnapshots.push({
      timestamp: Date.now(),
      trigger,
      state: sanitizedState,
    });

    // Keep only last 100 snapshots
    if (this.stateSnapshots.length > 100) {
      this.stateSnapshots = this.stateSnapshots.slice(-100);
    }
  }

  addEvent(event) {
    this.events.push(event);
    this.maintainCircularBuffer();
    this.updateStore();
  }

  addNetworkCall(call) {
    this.networkCalls.push(call);
    this.maintainCircularBuffer();
    this.updateStore();
  }

  updateNetworkCall(id, updates) {
    const call = this.networkCalls.find(c => c.id === id);
    if (call) {
      Object.assign(call, updates);
    }
  }

  addConsoleOutput(output) {
    this.consoleOutput.push(output);
    this.maintainCircularBuffer();
    this.updateStore();
  }

  maintainCircularBuffer() {
    // Keep total events under maxEvents
    const totalEvents = this.events.length + this.networkCalls.length + this.consoleOutput.length;

    if (totalEvents > this.maxEvents) {
      const overflow = totalEvents - this.maxEvents;

      // Remove oldest events proportionally
      const eventsToRemove = Math.floor(overflow * (this.events.length / totalEvents));
      const networkToRemove = Math.floor(overflow * (this.networkCalls.length / totalEvents));
      const consoleToRemove = overflow - eventsToRemove - networkToRemove;

      if (eventsToRemove > 0) {
        this.events = this.events.slice(eventsToRemove);
      }
      if (networkToRemove > 0) {
        this.networkCalls = this.networkCalls.slice(networkToRemove);
      }
      if (consoleToRemove > 0) {
        this.consoleOutput = this.consoleOutput.slice(consoleToRemove);
      }
    }
  }

  updateStore() {
    if (store.dispatch) {
      const totalCount = this.events.length + this.networkCalls.length + this.consoleOutput.length;
      store.dispatch(updateEventCount(totalCount));
    }
  }

  async saveSession() {
    if (!this.db) await this.initializeDB();

    const session = {
      timestamp: Date.now(),
      metadata: this.sessionMetadata,
      events: this.events,
      networkCalls: this.networkCalls,
      stateSnapshots: this.stateSnapshots,
      consoleOutput: this.consoleOutput,
    };

    const transaction = this.db.transaction(['sessions'], 'readwrite');
    const store = transaction.objectStore('sessions');

    return new Promise((resolve, reject) => {
      const request = store.add(session);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getLastSession() {
    if (!this.db) await this.initializeDB();

    const transaction = this.db.transaction(['sessions'], 'readonly');
    const store = transaction.objectStore('sessions');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const sessions = request.result;
        resolve(sessions[sessions.length - 1]);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearSessions() {
    if (!this.db) await this.initializeDB();

    const transaction = this.db.transaction(['sessions'], 'readwrite');
    const store = transaction.objectStore('sessions');

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
export const debugRecorder = new DebugRecorder();